import { Suspense, useEffect, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { CloseButton } from "@lecartte/shared-ui/components/RoundIconButton";
import { useSubscriptionPlans } from '@/features/subscription/hooks/useSubscriptionPlans';
import { useSubscribeFlow } from '@/features/subscription/hooks/useSubscribeFlow';
import { PlanListStep } from '@/features/subscription/components/steps/PlanListStep';
import { PaymentMethodStep } from '@/features/subscription/components/steps/PaymentMethodStep';
import { SavedCardSelectorStep } from '@/features/subscription/components/steps/SavedCardSelectorStep';
import { CardFormStep } from '@/features/subscription/components/steps/CardFormStep';
import { ResultStep } from '@/features/subscription/components/steps/ResultStep';
import { PromoCodeInput } from '@/features/subscription/components/PromoCodeInput';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, the modal opens pre-selected on this plan slug. */
  initialPlan?: string | null;
}

const PlanCardsSkeleton = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="card animate-pulse p-5 space-y-3">
        <div className="h-5 w-2/3 rounded bg-slate-200" />
        <div className="h-3 w-full rounded bg-slate-100" />
        <div className="h-7 w-1/2 rounded bg-slate-200" />
        <div className="space-y-2">
          {[1, 2, 3].map((j) => (
            <div key={j} className="h-3 w-full rounded bg-slate-100" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// White card wrapper. Width depends on the active step — wide for the plan list
// (3-column grid), narrow for the rest (single column of buttons / form / result).
const ModalShell = ({ width, onClose, children }: { width: 'wide' | 'narrow'; onClose: () => void; children: ReactNode }) => {
  const widthClass = width === 'wide' ? 'max-w-[1080px]' : 'max-w-md';
  return (
    <motion.div
      initial={{ opacity: 0, y: 48, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
      className={`relative z-10 flex w-full ${widthClass} max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl`}
    >
      <div className="absolute right-4 top-4 z-10">
        <CloseButton onClick={onClose} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-6">
        {children}
      </div>
    </motion.div>
  );
};

const ModalContent = ({ onClose, initialPlan }: { onClose: () => void; initialPlan?: string | null }) => {
  const { data, visiblePlans, isAsaas, currentPlan, savedCards } = useSubscriptionPlans();
  const flow = useSubscribeFlow({
    isAsaas,
    savedCards,
    plansForLookup: data.subscriptionPlans,
    initialPlan,
  });

  const width: 'wide' | 'narrow' = (flow.step.type === 'plans' || flow.step.type === 'card') ? 'wide' : 'narrow';

  const stepContent = (() => {
    switch (flow.step.type) {
      case 'result':
        return <ResultStep result={flow.step.result} onClose={onClose} />;

      case 'method':
        return (
          <PaymentMethodStep
            planName={flow.step.planName}
            valueCents={flow.step.valueCents}
            error={flow.error}
            isPending={flow.isPending}
            onPayWithCard={() => flow.goToCardStep({ planSlug: flow.step.type === 'method' ? flow.step.planSlug : '', planName: flow.step.type === 'method' ? flow.step.planName : '', valueCents: flow.step.type === 'method' ? flow.step.valueCents : 0 })}
            onPayWithPix={() => flow.submitSubscribe({ planSlug: flow.step.type === 'method' ? flow.step.planSlug : '' })}
            onBack={flow.handleBack}
          />
        );

      case 'card_selector':
        return (
          <SavedCardSelectorStep
            planName={flow.step.planName}
            valueCents={flow.step.valueCents}
            savedCards={flow.step.savedCards}
            selectedCardId={flow.selectedCardId}
            setSelectedCardId={flow.setSelectedCardId}
            autoChargeExcess={flow.autoChargeExcess}
            setAutoChargeExcess={flow.setAutoChargeExcess}
            error={flow.error}
            isPending={flow.isPending}
            onConfirm={({ savedCardId }) => flow.submitSubscribe({ planSlug: flow.step.type === 'card_selector' ? flow.step.planSlug : '', savedCardId })}
            onUseNewCard={() => flow.setStep({ type: 'card', planSlug: flow.step.type === 'card_selector' ? flow.step.planSlug : '', planName: flow.step.type === 'card_selector' ? flow.step.planName : '', valueCents: flow.step.type === 'card_selector' ? flow.step.valueCents : 0 })}
            onBack={flow.handleBack}
          />
        );

      case 'card':
        return (
          <CardFormStep
            planName={flow.step.planName}
            valueCents={flow.step.valueCents}
            card={flow.card}
            setCard={flow.setCard}
            holderInfo={flow.holderInfo}
            setHolderInfo={flow.setHolderInfo}
            autoChargeExcess={flow.autoChargeExcess}
            setAutoChargeExcess={flow.setAutoChargeExcess}
            error={flow.error}
            isPending={flow.isPending}
            onSubmit={() => flow.submitSubscribe({ planSlug: flow.step.type === 'card' ? flow.step.planSlug : '', creditCard: flow.card, creditCardHolderInfo: flow.holderInfo })}
            onBack={flow.handleBack}
            discount={flow.discountInfo}
            promoCodeSlot={
              <PromoCodeInput
                promotionalCode={flow.promotionalCode}
                planSlug={flow.step.type === 'card' ? flow.step.planSlug : ''}
                valueCents={flow.step.valueCents}
                onApplied={({ code, discount }) => { flow.setPromotionalCode(code); flow.setDiscountInfo(discount); }}
                onRemoved={() => { flow.setPromotionalCode(null); flow.setDiscountInfo(null); }}
              />
            }
          />
        );

      case 'plans':
      default:
        return (
          <PlanListStep
            plans={visiblePlans}
            currentPlan={currentPlan}
            isLoading={flow.isPending}
            error={flow.error}
            onPlanClick={flow.handlePlanClick}
          />
        );
    }
  })();

  return <ModalShell width={width} onClose={onClose}>{stepContent}</ModalShell>;
};

export const SubscriptionModal = ({ isOpen, onClose, initialPlan }: SubscriptionModalProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <Suspense
        fallback={
          <ModalShell width="wide" onClose={onClose}>
            <PlanCardsSkeleton />
          </ModalShell>
        }
      >
        <ModalContent onClose={onClose} initialPlan={initialPlan} />
      </Suspense>
    </div>
  );
};
